import type { RuntimeLintContext, RuntimeLintRule } from './runtimeRules';
import type { Pose } from './poseSignature';
import { posesPerceptiblyDiffer } from './poseSignature';
import { samplePoses } from './scenePass';

/**
 * Is the graphic alive across the whole scene?
 *
 * Measured from the composition alone, with no reference to a plan, so it also covers graphics
 * authored before the plan carried a schedule and graphics a user has edited since. Advice rather
 * than a block: a design can earn a longer hold than any threshold allows, and whether it has is not
 * a judgement a measurement can make.
 */

/**
 * A hold longer than this is where a graphic dies rather than a beat it is letting land.
 *
 * Held at three seconds against measured output. Scenes that read as well-paced have come back
 * with holds of up to four seconds where the copy that just landed still needed reading, so a
 * tighter bar reports pacing its author is happy with; at three it caught every graphic that had
 * genuinely stopped and the agent re-paced each one.
 */
const DEAD_STRETCH_SECONDS = 3;

/**
 * How long the last pose a scene reaches has to stay up before the cut. A graphic that resolves on
 * its final frame — a grid still repacking as the next scene arrives — is gone before the viewer has
 * taken in what it resolved into, however good the move was.
 */
const SETTLE_SECONDS = 1;

interface Sample {
    seconds: number;
    /** The whole frame. */
    pose: Pose;
    /** Only the graphic's content — see `contentFilter`. */
    contentPose: Pose;
}

function trim(value: number): string {
    return value.toFixed(1).replace(/\.0$/, '');
}

/**
 * The shared pass over the scene, placed on the clock these rules report in. Null without a real
 * scene duration to measure a stretch in, or when the composition cannot be posed at all.
 */
async function sampleScene(context: RuntimeLintContext): Promise<Sample[] | null> {
    const { sceneDurationMs } = context;
    if (
        typeof sceneDurationMs !== 'number' ||
        !Number.isFinite(sceneDurationMs) ||
        sceneDurationMs <= 0
    ) {
        return null;
    }
    const scene = await samplePoses(context);
    if (!scene) {
        return null;
    }
    const sceneSeconds = sceneDurationMs / 1000;
    return scene.poses.map(({ progress, pose, contentPose }) => ({
        seconds: progress * sceneSeconds,
        pose,
        contentPose,
    }));
}

/**
 * The stretches the frame passes without visibly changing, in order. A run of one sample spans no
 * time, so a composition that changes at every probe returns spans of zero rather than nothing.
 */
function stillRuns(
    samples: Sample[],
    of: 'pose' | 'contentPose' = 'pose',
): { from: number; to: number }[] {
    const runs: { from: number; to: number }[] = [];
    let start = 0;
    for (let i = 1; i < samples.length; i++) {
        const elapsed = samples[i].seconds - samples[i - 1].seconds;
        if (posesPerceptiblyDiffer(samples[i - 1][of], samples[i][of], elapsed)) {
            runs.push({ from: samples[start].seconds, to: samples[i - 1].seconds });
            start = i;
        }
    }
    runs.push({ from: samples[start].seconds, to: samples[samples.length - 1].seconds });
    return runs;
}

function longestRun(runs: { from: number; to: number }[]): { from: number; to: number } {
    return runs.reduce((a, b) => (b.to - b.from > a.to - a.from ? b : a), { from: 0, to: 0 });
}

/** A review looks at no more rests than this, keeping the longest; each is a frame it has to read. */
const MAX_REST_POINTS = 6;

/**
 * A moment inside each stretch the graphic's content holds still, as scene progress, in order — the
 * frames to judge a graphic on when a still image is the question. Measured from what renders, so a
 * beat retimed after planning moves its rest with it. Null when the content never holds still, or
 * the composition cannot be posed.
 */
export async function restingProgressPoints(context: RuntimeLintContext): Promise<number[] | null> {
    const samples = await sampleScene(context);
    if (!samples || samples.length < 2) {
        return null;
    }
    const sceneSeconds = samples[samples.length - 1].seconds;
    const rests = stillRuns(samples, 'contentPose')
        .filter(({ from, to }) => to > from)
        .sort((a, b) => b.to - b.from - (a.to - a.from))
        .slice(0, MAX_REST_POINTS)
        .sort((a, b) => a.from - b.from);
    return rests.length > 0 ? rests.map(({ from, to }) => (from + to) / 2 / sceneSeconds) : null;
}

export const motionDeadStretch: RuntimeLintRule = {
    id: 'motion-dead-stretch',
    severity: 'warning',
    guidance:
        'Keep something moving across the whole scene. Copy that has to be read holds its position ' +
        'once it lands — nobody can read something still travelling — but the next phase should ' +
        'begin rearranging the frame before it has time to feel static, so a graphic is never ' +
        `frozen for more than about ${DEAD_STRETCH_SECONDS} seconds together.`,
    async check(context: RuntimeLintContext): Promise<string[]> {
        const samples = await sampleScene(context);
        if (!samples || samples.length < 2) {
            return [];
        }
        const longest = longestRun(stillRuns(samples));
        const held = longest.to - longest.from;
        if (held <= DEAD_STRETCH_SECONDS) {
            return [];
        }
        return [
            `Nothing on screen changes between ${trim(longest.from)}s and ${trim(longest.to)}s — ` +
                `${trim(held)}s of a frozen frame while the scene keeps playing. Pace the graphic ` +
                `so the next thing starts before the frame has time to feel static: bring a later ` +
                `beat forward, give an element already on screen something to do as the next one ` +
                `arrives, or let the phase that lands here take longer to land.`,
        ];
    },
};

export const motionNoSettle: RuntimeLintRule = {
    id: 'motion-no-settle',
    severity: 'warning',
    guidance:
        `Let the graphic settle before the scene ends: its last move finishes at least ` +
        `${SETTLE_SECONDS}s before the cut, so what it resolved into is on screen long enough to ` +
        'take in.',
    async check(context: RuntimeLintContext): Promise<string[]> {
        const samples = await sampleScene(context);
        if (!samples || samples.length < 2) {
            return [];
        }
        const runs = stillRuns(samples, 'contentPose');
        const settledFrom = runs[runs.length - 1].from;
        const sceneSeconds = samples[samples.length - 1].seconds;
        const lastMoving = samples.filter((sample) => sample.seconds < settledFrom).pop();
        // The move stopped somewhere between the last sample that saw it and the first that
        // didn't, so the hold is judged at its longest: a move finished right on the bar must not
        // be reported for landing between two probes.
        const heldAtMost = sceneSeconds - (lastMoving?.seconds ?? settledFrom);
        if (heldAtMost >= SETTLE_SECONDS) {
            return [];
        }
        return [
            `The graphic is still moving at ${trim(lastMoving?.seconds ?? settledFrom)}s and the ` +
                `scene cuts at ${trim(sceneSeconds)}s, so the pose it ends on is up for under ` +
                `${heldAtMost.toFixed(2)}s — gone before anyone takes it in. Finish the last move by ` +
                `${trim(sceneSeconds - SETTLE_SECONDS)}s: start it earlier, make it quicker, or ` +
                `fold it into the phase before.`,
        ];
    },
};

export const CORE_MOTION_CONTINUITY_LINT_RULES: RuntimeLintRule[] = [
    motionDeadStretch,
    motionNoSettle,
];
